import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ScheduleItem } from '../../types/index.js'

/**
 * Sélection multiple sur la grille.
 *
 * Elle est **toujours disponible**, cadenas ouvert ou fermé : sélectionner ne
 * modifie rien, et l'opérateur doit pouvoir désigner une tranche d'antenne à
 * n'importe quel moment — y compris pendant la diffusion — pour la rediffuser
 * ou la vider. Le cadenas garde ce qu'il a toujours gardé : le déplacement et
 * l'ajout au pointeur.
 *
 * Trois gestes, ceux de tous les explorateurs de fichiers :
 *
 * - clic simple : la sélection devient cet élément seul ;
 * - Ctrl/Cmd + clic : ajoute ou retire cet élément ;
 * - Maj + clic : étend de l'ancre au clic, **dans l'ordre de l'antenne**. C'est
 *   l'ordre chronologique qui fait foi, pas l'ordre de rendu : sur la timeline
 *   deux éléments peuvent se côtoyer visuellement en colonnes parallèles.
 */

export type GridSelectMode = 'REPLACE' | 'TOGGLE' | 'RANGE'

export interface GridSelectionSpan {
  start:       string
  end:         string
  /** Durée de la plage bornes comprises — c'est elle que dure une boucle. */
  durationSec: number
}

export interface GridSelection {
  selectedIds:  string[]
  count:        number
  /** Éléments sélectionnés, triés dans l'ordre de l'antenne. */
  items:        ScheduleItem[]
  span:         GridSelectionSpan | null
  isSelected:   (id: string) => boolean
  select:       (item: ScheduleItem, mode: GridSelectMode) => void
  selectAll:    () => void
  clear:        () => void
}

/** Lit les modificateurs d'un clic. Une seule lecture, partagée par les vues. */
export function selectModeFromEvent(event: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean }): GridSelectMode {
  if (event.metaKey || event.ctrlKey) return 'TOGGLE'
  if (event.shiftKey) return 'RANGE'
  return 'REPLACE'
}

export function useGridSelection(items: ScheduleItem[]): GridSelection {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [anchorId, setAnchorId] = useState<string | null>(null)

  const chronological = useMemo(
    () => [...items].sort((first, second) =>
      new Date(first.startTime).getTime() - new Date(second.startTime).getTime()
      || first.sortOrder - second.sortOrder),
    [items],
  )

  // Une grille qui se rafraîchit — suppression, WebSocket, changement de jour —
  // ne doit pas laisser dans la sélection des identifiants qui n'existent plus :
  // la barre d'actions annoncerait un compte faux et la rediffusion partirait
  // sur des éléments introuvables.
  useEffect(() => {
    const alive = new Set(items.map(item => item.id))
    setSelectedIds(current => {
      const next = current.filter(id => alive.has(id))
      return next.length === current.length ? current : next
    })
    setAnchorId(current => (current && alive.has(current) ? current : null))
  }, [items])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const selectedItems = useMemo(
    () => chronological.filter(item => selectedSet.has(item.id)),
    [chronological, selectedSet],
  )

  const span = useMemo<GridSelectionSpan | null>(() => {
    if (selectedItems.length === 0) return null
    const start = Math.min(...selectedItems.map(item => new Date(item.startTime).getTime()))
    const end   = Math.max(...selectedItems.map(item => new Date(item.endTime).getTime()))
    return {
      start:       new Date(start).toISOString(),
      end:         new Date(end).toISOString(),
      durationSec: Math.round((end - start) / 1000),
    }
  }, [selectedItems])

  const select = useCallback((item: ScheduleItem, mode: GridSelectMode) => {
    if (mode === 'TOGGLE') {
      setSelectedIds(current => current.includes(item.id)
        ? current.filter(id => id !== item.id)
        : [...current, item.id])
      setAnchorId(item.id)
      return
    }

    if (mode === 'RANGE') {
      setSelectedIds(current => {
        const anchor = anchorId ?? current[current.length - 1] ?? item.id
        const from = chronological.findIndex(candidate => candidate.id === anchor)
        const to   = chronological.findIndex(candidate => candidate.id === item.id)
        if (from === -1 || to === -1) return [item.id]
        return chronological
          .slice(Math.min(from, to), Math.max(from, to) + 1)
          .map(candidate => candidate.id)
      })
      // L'ancre ne bouge pas : Maj + clic répété rétrécit ou élargit la même plage.
      setAnchorId(current => current ?? item.id)
      return
    }

    setSelectedIds([item.id])
    setAnchorId(item.id)
  }, [anchorId, chronological])

  const selectAll = useCallback(() => {
    setSelectedIds(chronological.map(item => item.id))
    setAnchorId(chronological[0]?.id ?? null)
  }, [chronological])

  const clear = useCallback(() => {
    setSelectedIds([])
    setAnchorId(null)
  }, [])

  const isSelected = useCallback((id: string) => selectedSet.has(id), [selectedSet])

  return {
    selectedIds,
    count: selectedIds.length,
    items: selectedItems,
    span,
    isSelected,
    select,
    selectAll,
    clear,
  }
}
